import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Search, Plus, Check } from "lucide-react";

const err = (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message);

export function FicheDialog({ texte, onClose, onSaved }) {
  const [f, setF] = useState({ texte: "", url: "" });
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const body = f.texte.trim() ? { texte: f.texte } : f.url.trim() ? { url: f.url.trim() } : {};
      await api.post(`/veille/${texte.id}/fiche`, body);
      toast.success("Fiche rédigée par ZOE"); setF({ texte: "", url: "" }); onSaved();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!texte} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="veille-fiche-dialog">
        <DialogHeader><DialogTitle>Fiche ZOE — {texte?.titre}</DialogTitle>
          <DialogDescription>Collez le texte (recommandé pour Légifrance) ou indiquez un lien lisible. Sans saisie, ZOE lit le lien enregistré.</DialogDescription></DialogHeader>
        <Textarea rows={8} placeholder="Texte de l'article, du décret, de la circulaire…" value={f.texte} onChange={(e) => setF({ ...f, texte: e.target.value })} data-testid="veille-fiche-texte" />
        <Input placeholder="ou lien https://…" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} data-testid="veille-fiche-url" />
        <DialogFooter><Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={run} disabled={busy || (!f.texte.trim() && !f.url.trim() && !texte?.url)} data-testid="veille-fiche-generer" className="bg-[#C5A059] hover:bg-[#A88846] text-white">
            {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Rédiger la fiche</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const VIDE = { titre: "", reference: "", url: "", theme: "Autre", date_echeance: "", resume: "" };

export function AjoutTexteDialog({ open, onClose, onSaved }) {
  const [f, setF] = useState(VIDE);
  const save = async () => {
    const body = Object.fromEntries(Object.entries(f).filter(([, v]) => v.trim()));
    try { await api.post("/veille", body); toast.success("Texte ajouté à la veille"); setF(VIDE); onSaved(); } catch (e) { err(e); }
  };
  const field = (k, ph, type = "text") => <Input type={type} placeholder={ph} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`veille-add-${k}`} />;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="veille-add-dialog">
        <DialogHeader><DialogTitle>Ajouter un texte à la veille</DialogTitle><DialogDescription>Loi, décret, arrêté, circulaire DEETS, convention collective…</DialogDescription></DialogHeader>
        <div className="space-y-3">
          {field("titre", "Titre")}{field("reference", "Référence (ex. C. trav. L1253-9)")}{field("url", "Lien https://…")}
          {field("theme", "Thème")}<label className="text-xs text-slate-500 block">Échéance (facultatif)</label>{field("date_echeance", "", "date")}
          <Textarea rows={3} placeholder="Résumé (facultatif)" value={f.resume} onChange={(e) => setF({ ...f, resume: e.target.value })} data-testid="veille-add-resume" />
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={f.titre.trim().length < 3} data-testid="veille-add-save" className="bg-[#C5A059] hover:bg-[#A88846] text-white">Ajouter</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RechercheDialog({ open, canEdit, onClose, onSaved }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const chercher = async () => {
    setBusy(true);
    try { const { data } = await api.get("/veille/recherche", { params: q.trim() ? { q } : {} }); setRes(data); } catch (e) { err(e); } finally { setBusy(false); }
  };
  const ajouter = async (it) => {
    try {
      await api.post("/veille", { titre: it.titre, url: it.url, guid: it.guid, resume: it.extrait, date_publication: it.date_publication, origine: "recherche", theme: "Actualité" });
      setRes((r) => ({ ...r, items: r.items.map((x) => (x.guid === it.guid ? { ...x, deja_suivi: true } : x)) }));
      toast.success("Ajouté à la veille"); onSaved();
    } catch (e) { err(e); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="veille-recherche-dialog">
        <DialogHeader><DialogTitle>Recherche dans les sources officielles</DialogTitle>
          <DialogDescription>Actualités professionnelles Service-Public Entreprendre (DILA). Sans mots-clés : filtres « droit social GE » par défaut.</DialogDescription></DialogHeader>
        <div className="flex gap-2">
          <Input placeholder="mots-clés séparés par des virgules (facultatif)" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && chercher()} data-testid="veille-recherche-q" />
          <Button onClick={chercher} disabled={busy} data-testid="veille-recherche-go">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}</Button>
        </div>
        {res && <div className="space-y-2" data-testid="veille-recherche-resultats">
          <div className="text-xs text-slate-500">{res.total_exact} résultat(s). {res.note}</div>
          {res.items.map((it) => (
            <div key={it.guid} className="border border-slate-200 rounded-lg p-3 flex gap-3 items-start" data-testid="veille-recherche-item">
              <div className="flex-1 min-w-0">
                <a href={it.url} target="_blank" rel="noreferrer" className="text-sm font-medium text-[#17352e] hover:underline">{it.titre}</a>
                <div className="text-xs text-slate-500">{it.date_publication} · {it.mots_cles.join(", ")}</div>
                <div className="text-xs text-slate-600 mt-1 line-clamp-2">{it.extrait}</div>
              </div>
              {canEdit && (it.deja_suivi ? <span className="text-xs text-emerald-700 flex items-center gap-1"><Check className="h-3 w-3" />Suivi</span>
                : <Button size="sm" variant="outline" onClick={() => ajouter(it)} data-testid="veille-recherche-ajouter"><Plus className="h-3 w-3 mr-1" />Suivre</Button>)}
            </div>
          ))}
        </div>}
      </DialogContent>
    </Dialog>
  );
}
